// E³-Survey 組織結果の6タブ（プロトタイプ v3.0〜v3.2 の orgOverview / orgScore / orgFactor / orgCompare /
// orgTrend / orgComment を React に移植）。データは POST /api/results/30010/org の応答。
//   d        : 今回の集計（node）
//   groupRef : グループ全体の平均 { cats, items }（部署を割り当てられたシーズン管理者は null。系列会社を選んでもグループ全体のまま）
//   questions: 設問文（q → 文）
//   query    : 比較・推移・コメントの API に渡す条件（グループ・シーズン・所属・属性。系列会社ありグループは company も）
//   compare  : 属性別の比較（浅い所属のときだけ。6名未満はサーバーでマスク済み）
// オーバービュー・スコア・要因分析・推移・コメントは、改修希望 v1.1（E3-08〜10・12・13・2026-10-13）のカード構成。
import React, { useEffect, useState } from 'react';
import Axios from 'axios';
import { Cascader, Input, Select, Tooltip, message } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import {
  AREAS,
  CATS,
  CAT_BY,
  CAT_DEF_SHORT,
  CAT_OF_Q,
  CAT_QS,
  CORR_TH,
  DEF,
  DIST_CAPS,
  EMP_OF as EMP_OF_UI,
  FACTS_OF as FACTS_OF_UI,
  FACT_CATS,
  FQ,
  FQ_ORDER,
  RANK_RANGE,
  RANK_STATE,
  TYPE_COLOR,
  TYPE_INFO,
  TYPE_LINE_COLOR,
  TYPE_ORDER,
  fqOf,
  fx1,
  grade,
  itemName,
  rkCls,
} from './e3Const';
import {
  AreaColumns,
  Measure,
  PmTip,
  PriorityMatrix,
  RankLegend,
  TREND_WHY,
  TrendChart,
  endLabel,
  TypeLegend,
  TypeQuad,
  corrUnavailable,
  empItemEntries,
  factEntries,
  factItemEntries,
} from './e3Charts';

const API = '/api/results/30010';

// 「?」の説明。そのカードに出ている内容は書かず、画面からは分からない内容（定義・計算のしかた・境目の扱い・表示の決まり）だけを書く
// （改修希望 E3-14 の 3。文はプロトタイプ v3.2 の HELP）
const HelpLine = ({ children, last }) => <div style={{ marginBottom: last ? 0 : 6 }}>{children}</div>;
const HELP_CONV = (
  <HelpLine>
    <b>回答の換算</b>　7段階の回答を、1＝0、4＝50、7＝100 になるように換算します（（回答−1）÷6×100）。
  </HelpLine>
);
const HELP_TYPES = TYPE_ORDER.map((t, i) => (
  <HelpLine key={t} last={i === TYPE_ORDER.length - 1}>
    <b>{t}タイプ</b>　{TYPE_INFO[t].def}
  </HelpLine>
));
const HELP = {
  scoreOrg: (
    <>
      <HelpLine>
        <b>総合共感（エンゲージメント）</b>　{DEF.total}。
      </HelpLine>
      <HelpLine>
        <b>計算のしかた</b>
        　会社・上司・職場・職務の4領域の共感度の平均です。共感度は、その領域の共感の設問5問の回答を0〜100に換算し、回答者で平均した値です。
      </HelpLine>
      {HELP_CONV}
      <HelpLine last>
        <b>ランクの境目</b>　スコア幅の下の値以上で、そのランクになります（84.0以上がA、83.9はB）。
      </HelpLine>
    </>
  ),
  typeOrg: (
    <>
      <HelpLine>
        回答者を、組織共感と職務共感の高低で4つのタイプに分けています。境界は各軸65で、65ちょうどのときは低い側に含めます。
      </HelpLine>
      <HelpLine>
        <b>組織共感</b>　会社・上司・職場の共感度の平均　／　<b>職務共感</b>　職務の共感度
      </HelpLine>
      {HELP_TYPES}
      <HelpLine last>図の目盛りは、境界の65が中央に来るように引き伸ばしています。</HelpLine>
    </>
  ),
  typeLayer: (
    <>
      <HelpLine>
        回答者を4つのエンゲージメントタイプに分け、タイプごとに、事実の中項目20項目のスコアの平均を線にしています。タイプのあいだで線が離れている項目ほど、タイプによってスコアに差がある項目です。
      </HelpLine>
      <HelpLine>
        <b>タイプの分け方</b>
        　組織共感（会社・上司・職場の共感度の平均）と職務共感（職務の共感度）を、それぞれ65で区切ります。65ちょうどのときは低い側に含めます。
        <br />
        いきいき活躍＝どちらも65より高い　／　つながり重視＝組織共感だけ65より高い
        <br />
        じっくり集中＝職務共感だけ65より高い　／　ひとやすみ充電＝どちらも65以下
      </HelpLine>
      <HelpLine last>縦軸は0からではなく、線の最も低い値と最も高い値に合わせて範囲を絞っています。</HelpLine>
    </>
  ),
  area: (
    <>
      <HelpLine>
        <b>共感度</b>　その領域の共感の設問5問の平均です。
      </HelpLine>
      <HelpLine>
        <b>事実度</b>　その領域の事実の中項目5つ（設問25問）の平均です。
      </HelpLine>
      <HelpLine>
        <b>組織</b>　会社・上司・職場の3領域の平均です。共感度の欄は「組織共感（組織エンゲージメント）」にあたり、{DEF.org}を表します。
      </HelpLine>
      <HelpLine last>
        <b>職務</b>　共感度の欄は「職務共感（ワーク・エンゲージメント）」にあたり、{DEF.job}を表します。
      </HelpLine>
    </>
  ),
  comment: (
    <>
      <HelpLine>
        コメントは匿名です。回答の順が分からないように並びを入れ替え、日付と書き手の属性（会社・部署・役職）は表示しません。
      </HelpLine>
      <HelpLine>
        <b>件数と記入率</b>　「特になし」などの回答は、件数に含めず、一覧にも出しません。記入率は、件数 ÷ 回答者数です。
      </HelpLine>
      <HelpLine>
        <b>総合共感スコアの層</b>
        　いまの条件（所属の絞り込み）に入る回答者を、総合共感スコアの中央値で2つに分けます。中央値以上が上位50%、中央値未満が下位50%です。属性で絞り込んでいるときと、どちらかの層が6名未満のときは使えません。
      </HelpLine>
      <HelpLine last>
        <b>表示の決まり</b>　1つの領域で表示するのは300件までです。回答者が6名未満のときは、本文を表示しません。
      </HelpLine>
    </>
  ),
  factor: (
    <>
      <HelpLine>
        <b>影響度</b>
        　事実の中項目のスコアと、同じ領域の共感度との相関係数（−1〜1）です。高いほど、その項目のスコアが高い人は共感度も高い、という関係が強いことを表します。因果関係を示すものではありません。
      </HelpLine>
      <HelpLine>
        <b>区分の分け方</b>　スコアが平均（事実の中項目20項目の平均）以上か未満かと、影響度が{CORR_TH}以上か未満かの組み合わせで分けます。
        {FQ_ORDER.map((k) => (
          <React.Fragment key={k}>
            <br />
            {FQ[k].name}＝{FQ[k].cond}
          </React.Fragment>
        ))}
      </HelpLine>
      <HelpLine>
        <b>見る順</b>　影響度が{CORR_TH}
        以上の、改善項目と強化項目を先に見ます。改善項目は、すでに取り組みがあり、内容の見直しから始められる項目です。強化項目は、取り組みそのものが足りていない項目です。
      </HelpLine>
      <HelpLine last>区分の中は、影響度の高い順に並べています。回答者が3名未満のときは、影響度を計算できません。</HelpLine>
    </>
  ),
};
// 説明の枠は、薄いグレーの地に黒字・枠・影（改修希望 E3-14 の 1。依頼側の希望で、サイトのほかの画面の黒地とは変える）。
// 出し方はマウスを重ねると出る形のまま（2026-10-15 決定）
const TIP_BODY = {
  color: 'var(--black)',
  border: '1px solid var(--grey-30)',
  borderRadius: 8,
  padding: '10px 14px',
  fontSize: 'var(--text-sm)',
  lineHeight: 1.6,
  boxShadow: '0 6px 16px 0 rgba(0,0,0,.08), 0 3px 6px -4px rgba(0,0,0,.12), 0 9px 28px 8px rgba(0,0,0,.05)',
};
const Help = ({ k, label }) => (
  <Tooltip title={HELP[k]} color="var(--grey-10)" styles={{ root: { maxWidth: 520 }, body: TIP_BODY }}>
    <QuestionCircleOutlined className="e3r-help" aria-label={label} />
  </Tooltip>
);

const c2 = (v) => (v != null ? v.toFixed(2) : '–');
// 分布図の区画と同じ色（タイプの色を 45% の濃さにした色）
const tint = (hex, a) =>
  `#${[1, 3, 5]
    .map((i) =>
      Math.round(parseInt(hex.slice(i, i + 2), 16) * a + 255 * (1 - a))
        .toString(16)
        .padStart(2, '0')
    )
    .join('')}`;

// 「事実項目ごとのスコア」などの縦棒のカード（横いっぱい）。点線＝グループ全体の平均（ref が無ければ出さない）
const ColumnsCard = ({ title, help, entries, groups, aria, hasRef }) => (
  <div className="as-card s4 ov-facts">
    <h3 className="as-card-title">
      {title}
      {help}
    </h3>
    <Measure className="fc-draw">{({ w }) => <AreaColumns entries={entries} w={w} aria={aria} groups={groups} />}</Measure>
    <div className="as-legend tq-legend fc-legend">
      <span className="lg">
        <i className="mk sq up" />
        強み（上位3）
      </span>
      <span className="lg">
        <i className="mk sq down" />
        弱み（下位3）
      </span>
      <span className="lg">
        <i className="mk sq" />
        そのほか
      </span>
      {hasRef && (
        <span className="lg">
          <i className="mk ln" />
          グループ全体の平均
        </span>
      )}
    </div>
  </div>
);
const FactsCard = ({ d, groupRef }) => (
  <ColumnsCard
    title="事実項目ごとのスコア"
    entries={factEntries(d, groupRef && groupRef.cats)}
    aria="事実の中項目20項目のスコア"
    hasRef={!!groupRef}
  />
);
// 分布図の凡例（改善項目・強化項目・ほか）
const FqLegend = () => (
  <div className="as-legend tq-legend">
    <span className="lg">
      <i className="mk fq-pri" />
      {FQ.pri.name}
    </span>
    <span className="lg">
      <i className="mk fq-build" />
      {FQ.build.name}
    </span>
    <span className="lg">
      <i className="mk fq-etc" />
      {FQ.watch.name}・{FQ.keep.name}
    </span>
  </div>
);

// ------------------------------------------------------------------
// 1. オーバービュー（カード 5 枚・改修希望 E3-08）
//   1 段目：［総合共感スコア｜推測される組織状態］［領域ごとの共感度と事実度］
//   2 段目：［事実項目ごとのスコア］（横いっぱい）
//   3 段目：［エンゲージメントタイプの構成］［エンゲージメントを高めるための改善項目］（高さ 728px）
export const OverviewTab = ({ d, groupRef }) => {
  const [tip, setTip] = useState(null);
  const g = grade(d.total);
  const gc = (g || '').toLowerCase();
  const sdBar = (v, cls) => (
    <td className="sd-v">
      <div className="sd-cell">
        <span className="sd-plot">
          <i className={`${cls}${v != null && v < 16 ? ' out' : ''}`} style={{ width: `${v == null ? 0 : Math.max(0, Math.min(100, v))}%` }}>
            <b className="num">{fx1(v)}</b>
          </i>
        </span>
      </div>
    </td>
  );
  // 差＝共感度−事実度（符号つき）
  const sdDiff = (e, f) => {
    if (e == null || f == null) return '–';
    const v = Math.round((e - f) * 10) / 10;
    return `${v > 0 ? '+' : v < 0 ? '−' : '±'}${Math.abs(v).toFixed(1)}`;
  };
  const dRow = (label, e, f, ind) => (
    <tr key={label} className={ind ? 'ind' : undefined}>
      <td className="sd-n">{label}</td>
      {sdBar(e, 'emp')}
      {sdBar(f, 'fact')}
      <td className="sd-d num">{sdDiff(e, f)}</td>
    </tr>
  );
  const sdAxis = (
    <td className="sd-v">
      <div className="sd-cell">
        <span className="sd-axis">
          <span>0</span>
          <span>25</span>
          <span>50</span>
          <span>75</span>
          <span>100</span>
        </span>
      </div>
    </td>
  );
  const td = d.typeDist || {};
  const tn = TYPE_ORDER.reduce((a, t) => a + (td[t] || 0), 0) || 1;
  return (
    <div className="g4 ov">
      <div className="as-card s2 ov-head">
        <div className="ov-col">
          <h3 className="as-card-title ov-ttl">
            <span>総合共感スコア</span>
            <span>
              （エンゲージメント）
              <Help k="scoreOrg" label="スコアの読み方" />
            </span>
          </h3>
          <div className="ov-donut">
            <div className={`as-donut xl rk-ring rk-${gc}`} style={{ '--p': d.total }} role="img" aria-label={`総合共感スコア ${fx1(d.total)}（100まで）`}>
              <b>
                {fx1(d.total)}
                <small>/100</small>
              </b>
            </div>
            <div className="ov-rank">
              <span className="kl">ランク</span>
              <b className={`rk-badge rk-big rk-${gc}`}>{g}</b>
            </div>
          </div>
        </div>
        <div className="ov-col ov-wide">
          <h3 className="as-card-title">推測される組織状態</h3>
          <table className="mtbl rank-tbl">
            <thead>
              <tr>
                <th>ランク</th>
                <th className="rg">スコア幅</th>
                <th className="st">推測される組織状態</th>
              </tr>
            </thead>
            <tbody>
              {Object.keys(RANK_RANGE).map((k) => (
                <tr key={k} className={`rk-${k.toLowerCase()}${k === g ? ' on' : ''}`} aria-current={k === g ? 'true' : undefined}>
                  <td className="rk">{k}</td>
                  <td className="rg">{RANK_RANGE[k]}</td>
                  <td className="st">{RANK_STATE[k] || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="as-card s2 ov-sd">
        <h3 className="as-card-title">
          領域ごとの共感度と事実度
          <Help k="area" label="事実度と共感度の説明" />
        </h3>
        <table className="mtbl sd-tbl">
          <thead>
            <tr>
              <th>領域</th>
              <th>
                共感度<small className="sd-note">（その領域にどれだけ共感しているか）</small>
              </th>
              <th>
                事実度<small className="sd-note">（その領域の実態がどれだけ整っているか）</small>
              </th>
              <th className="sd-d">
                差<small>（共感度−事実度）</small>
              </th>
            </tr>
          </thead>
          <tbody>
            {dRow('組織', d.org, d.factOrg, false)}
            {['会社', '上司', '職場'].map((a) => dRow(a, d.emp[a], d.fact[a], true))}
            {dRow('職務', d.job, d.fact['職務'], false)}
          </tbody>
          <tfoot>
            <tr>
              <td />
              {sdAxis}
              {sdAxis}
              <td />
            </tr>
          </tfoot>
        </table>
      </div>
      <FactsCard d={d} groupRef={groupRef} />
      <div className="as-card s2 ov-pair ov-tall">
        <h3 className="as-card-title">
          エンゲージメントタイプの構成
          <Help k="typeOrg" label="タイプの説明" />
        </h3>
        <div className="tp-row">
          {TYPE_ORDER.map((t) => (
            <div key={t} className="tp" style={{ background: tint(TYPE_COLOR[t], 0.45) }}>
              <span className="tp-h">{t}タイプ</span>
              <span className="tp-l">
                <b className="tp-v num">
                  {(((td[t] || 0) / tn) * 100).toFixed(1)}
                  <small>%</small>
                </b>
                <span className="tp-n">（{(td[t] || 0).toLocaleString()}名）</span>
              </span>
            </div>
          ))}
        </div>
        <Measure className="tq-fill" fill>
          {(size) => <TypeQuad size={size} pts={d.scatter || []} marks={[{ org: d.org, job: d.job }]} />}
        </Measure>
        <TypeLegend kinds={['dots', 'orgAvg']} />
      </div>
      <div className="as-card s2 ov-pair ov-tall">
        <h3 className="as-card-title">エンゲージメントを高めるための改善項目</h3>
        <Measure className="tq-fill" fill>
          {(size) => <PriorityMatrix d={d} size={size} onTip={(data, el) => setTip(data ? { data, el } : null)} />}
        </Measure>
        <FqLegend />
      </div>
      <PmTip tip={tip} />
    </div>
  );
};

// ------------------------------------------------------------------
// 2. スコア（改修希望 E3-09）
//   共感項目ごとのスコア → 共感項目のランキング｜共感項目の回答分布 → 事実項目ごとのスコア → 事実項目の強み｜弱み
//   → 領域ごとに（会社・上司・職場・職務）：事実項目【領域】ごとのスコア → 事実項目【領域】のランキング｜回答分布
// 順位の表（列：順位｜領域（領域ごとの表は中項目）｜小項目｜設問内容｜スコア｜分散）。高さ 364px・入りきらない行は枠の中でスクロール
const RankCard = ({ title, rows, rankOf, byCat, d, last }) => (
  <div className="as-card s2 sc-h sc-rank">
    <div className="ttl-row">
      <h3 className="as-card-title">{title}</h3>
      <RankLegend />
    </div>
    <div className="pri-scroll">
      <table className="mtbl pri-tbl">
        <thead>
          <tr>
            <th className="pr">順位</th>
            <th className="pa">{byCat ? '中項目' : '領域'}</th>
            <th className="ps">小項目</th>
            <th>設問内容</th>
            <th className="num">スコア</th>
            <th className="num">{last ? last[0] : '分散'}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((x, i) => (
            <tr key={x.q}>
              <td className="pr">{rankOf(i)}</td>
              <td className="pa">{byCat ? x.c.name : x.c.area}</td>
              <td className="ps">{itemName(x.q)}</td>
              <td className="pd">{x.text}</td>
              <td className={`num rk-cell ${rkCls(x.s)}`}>
                <b>{fx1(x.s)}</b>
              </td>
              <td className="num">
                {last ? <b>{last[1](x)}</b> : d.itemVar && d.itemVar[x.q] != null ? d.itemVar[x.q].toFixed(2) : '–'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

// 回答分布のカード（共感項目・領域ごとの事実項目で共通）。「項目」で設問を選ぶと、その設問の回答（1〜7）の構成比になる
const DistCard = ({ title, cats, allLabel, groupOf, d, questions }) => {
  const [cur, setCur] = useState('');
  const allQs = cats.flatMap((c) => CAT_QS[c.key]);
  const selQ = allQs.includes(+cur) ? +cur : null;
  const qs = selQ ? [selQ] : allQs;
  const hist = [0, 0, 0, 0, 0, 0, 0];
  qs.forEach((q) => ((d.itemHist && d.itemHist[q]) || []).forEach((v, i) => (hist[i] += v)));
  const total = hist.reduce((a, b) => a + b, 0) || 1;
  const mx = Math.max(...hist) || 1;
  const groups = [];
  cats.forEach((c) => {
    const label = groupOf(c);
    let gr = groups.find((x) => x.label === label);
    if (!gr) {
      gr = { label, options: [] };
      groups.push(gr);
    }
    CAT_QS[c.key].forEach((q) => gr.options.push({ value: String(q), label: itemName(q) }));
  });
  const text = selQ && questions ? questions[selQ] : '';
  return (
    <div className="as-card s2 sc-h sc-dist">
      <h3 className="as-card-title">{title}</h3>
      <div className="dist-pick">
        <label>項目</label>
        <Select
          className="e3r-select dist-sel"
          value={cur}
          onChange={setCur}
          popupMatchSelectWidth={false}
          options={[{ value: '', label: `${allLabel}（${allQs.length}問）` }, ...groups]}
        />
        {text && (
          <span className="dist-text" title={text}>
            {text}
          </span>
        )}
      </div>
      <div className="dist-row">
        {[
          ['否定的', '1〜3', hist.slice(0, 3)],
          ['中立', '4', hist.slice(3, 4)],
          ['肯定的', '5〜7', hist.slice(4)],
        ].map(([l, r, a]) => (
          <div key={l} className="dist-t">
            <span className="tp-h">
              {l}（{r}）
            </span>
            <b className="tp-v num">
              {((a.reduce((x, y) => x + y, 0) / total) * 100).toFixed(1)}
              <small>%</small>
            </b>
          </div>
        ))}
      </div>
      <div className="hist-plot dist-plot">
        <div className="hist-vaxis">
          <span>{Math.round((mx / total) * 100)}%</span>
          <span style={{ alignSelf: 'flex-end' }}>0%</span>
        </div>
        <div className="hist-cols" style={{ gridTemplateColumns: 'repeat(7,1fr)' }}>
          {hist.map((h, i) => (
            <div key={i} className="hcol">
              <div className="hist-bar" style={{ height: `${(h / mx) * 100}%` }}>
                <span className="hist-n">{Math.round((h / total) * 100)}%</span>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="hist-caps" style={{ gridTemplateColumns: 'repeat(7,1fr)' }}>
        {DIST_CAPS.map((l) => (
          <span key={l}>
            {l.split('\n').map((x, i) => (
              <React.Fragment key={i}>
                {i > 0 && <br />}
                {x}
              </React.Fragment>
            ))}
          </span>
        ))}
      </div>
    </div>
  );
};

export const ScoreTab = ({ d, groupRef, questions }) => {
  const gItems = groupRef ? groupRef.items : null;
  const itemRows = Object.entries(d.items || {})
    .map(([q, s]) => ({ q: +q, s, c: CAT_BY[CAT_OF_Q[q]], text: (questions && questions[q]) || `設問${q}` }))
    .filter((x) => x.c);
  const empRank = itemRows.filter((x) => x.c.kind === '共感').sort((a, b) => b.s - a.s);
  const factSorted = itemRows.filter((x) => x.c.kind === '事実').sort((a, b) => b.s - a.s);
  return (
    <div className="g4">
      <ColumnsCard title="共感項目ごとのスコア" entries={empItemEntries(d, gItems)} aria="共感の小項目20問のスコア" hasRef={!!gItems} />
      <RankCard title="共感項目のランキング" rows={empRank} rankOf={(i) => i + 1} d={d} />
      <DistCard
        title="共感項目の回答分布"
        cats={CATS.filter((c) => c.kind === '共感')}
        allLabel="共感項目すべて"
        groupOf={(c) => c.area}
        d={d}
        questions={questions}
      />
      <FactsCard d={d} groupRef={groupRef} />
      <RankCard title="事実項目の強み" rows={factSorted.slice(0, 10)} rankOf={(i) => i + 1} d={d} />
      <RankCard title="事実項目の弱み" rows={factSorted.slice(-10).reverse()} rankOf={(i) => factSorted.length - i} d={d} />
      {AREAS.map((a) => (
        <React.Fragment key={a}>
          <ColumnsCard
            title={`事実項目【${a}】ごとのスコア`}
            entries={factItemEntries(d, a, gItems)}
            groups={FACT_CATS.filter((c) => c.area === a).map((c) => c.name)}
            aria={`${a}の事実の小項目25問のスコア`}
            hasRef={!!gItems}
          />
          <RankCard title={`事実項目【${a}】のランキング`} rows={factSorted.filter((x) => x.c.area === a)} rankOf={(i) => i + 1} byCat d={d} />
          <DistCard
            title={`事実項目【${a}】の回答分布`}
            cats={FACT_CATS.filter((c) => c.area === a)}
            allLabel={`事実項目【${a}】すべて`}
            groupOf={(c) => c.name}
            d={d}
            questions={questions}
          />
        </React.Fragment>
      ))}
    </div>
  );
};

// ------------------------------------------------------------------
// 3. 要因分析（上から下へ詳しくなる 8 枚・改修希望 E3-10）
//   区分（取り組み区分）→ 中項目（分布図｜一覧）→ 小項目（領域ごとの影響度ランキング ×4）→ タイプ別（事実項目スコアの折れ線）
export const FactorTab = ({ d, questions }) => {
  const noCorr = corrUnavailable(d);
  const cats = FACT_CATS.map((c) => {
    const s = d.cats[c.key];
    const r = d.corr ? d.corr[c.key] : null;
    return { c, s, r, k: fqOf(s, r, d.factAvg) };
  });
  const byQ = (k) => cats.filter((x) => x.k === k).sort((a, b) => b.r - a.r);
  const QI = Object.fromEntries(FQ_ORDER.map((k, i) => [k, i]));
  const tblRows = cats
    .slice()
    .sort((a, b) => (a.k ? QI[a.k] : 9) - (b.k ? QI[b.k] : 9) || (b.r == null ? -9 : b.r) - (a.r == null ? -9 : a.r));
  // 領域ごとの小項目（事実 25 問）の影響度（サーバーの itemCorr）
  const items = FACT_CATS.flatMap((c) =>
    CAT_QS[c.key].map((q) => ({
      q,
      c,
      s: d.items ? d.items[q] : null,
      r: d.itemCorr ? d.itemCorr[q] : null,
      text: (questions && questions[q]) || `設問${q}`,
    }))
  );
  const byR = (a, b) => (b.r == null ? -9 : b.r) - (a.r == null ? -9 : a.r) || (b.s == null ? -1 : b.s) - (a.s == null ? -1 : a.s);
  // エンゲージメントタイプごとの事実項目スコア（少人数のタイプはサーバーで伏せている）
  const tc = d.typeCats || {};
  const typeOk = (t) => tc[t] && !tc[t].masked;
  const typeLines = TYPE_ORDER.filter(typeOk).map((t) => ({
    name: `${t}タイプ`,
    color: TYPE_LINE_COLOR[t],
    vals: Object.fromEntries(FACT_CATS.map((c) => [c.key, tc[t].cats[c.key]])),
  }));
  const typeEntries = FACT_CATS.map((c) => ({ id: c.key, name: `${c.key} ${c.name}`, label: c.name.replace('（', '|（'), area: c.area, v: null, g: null }));
  return (
    <div className="g4">
      <div className="as-card s4 fq-card">
        <h3 className="as-card-title">
          事実項目の取り組み区分
          <Help k="factor" label="影響度と区分の説明" />
        </h3>
        {noCorr ? (
          <p className="as-hint">{noCorr}</p>
        ) : (
          <div className="fq-row">
            {FQ_ORDER.map((k) => {
              const xs = byQ(k);
              return (
                <div key={k} className={`fq fq-${k}`}>
                  <div className="fq-h">
                    <b className="fq-n">{FQ[k].name}</b>
                    <span className="fq-a">（{FQ[k].act}）</span>
                    <span className="fq-c num">
                      {xs.length}
                      <small>項目</small>
                    </span>
                  </div>
                  <div className="fq-items">
                    {xs.length ? (
                      xs.map((x) => (
                        <span key={x.c.key} className="fq-it" title={`スコア ${fx1(x.s)}／影響度 ${c2(x.r)}`}>
                          <span className="pk">{x.c.key}</span>
                          {x.c.name}
                        </span>
                      ))
                    ) : (
                      <span className="fq-none">該当なし</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <div className="as-card s2 ov-pair ov-tall">
        <h3 className="as-card-title">事実項目のスコアと影響度の分布</h3>
        <Measure className="tq-fill" fill>
          {(size) => <PriorityMatrix d={d} size={size} />}
        </Measure>
        <FqLegend />
      </div>
      <div className="as-card s2 ov-tall ov-pri">
        <div className="ttl-row">
          <h3 className="as-card-title">事実項目のスコアと影響度の一覧</h3>
          <RankLegend />
        </div>
        {noCorr && <p className="as-hint">{noCorr}</p>}
        <div className="pri-scroll">
          <table className="mtbl pri-tbl">
            <thead>
              <tr>
                <th className="pq">区分</th>
                <th className="pa">領域</th>
                <th className="pn">中項目</th>
                <th>定義</th>
                <th className="num">スコア</th>
                <th className="num">影響度</th>
              </tr>
            </thead>
            <tbody>
              {tblRows.map((x) => (
                <tr key={x.c.key} className={x.k === 'pri' ? 'on' : x.k === 'build' ? 'sub' : undefined}>
                  <td className="pq">{x.k ? <span className={`fq-tag fq-${x.k}`}>{FQ[x.k].name}</span> : '–'}</td>
                  <td className="pa">{x.c.area}</td>
                  <td className="pn">
                    <span className="pk">{x.c.key}</span>
                    {x.c.name}
                  </td>
                  <td className="pd">{CAT_DEF_SHORT[x.c.key] || x.c.def}</td>
                  <td className={`num rk-cell ${rkCls(x.s)}`}>
                    <b>{fx1(x.s)}</b>
                  </td>
                  <td className="num">
                    <b>{c2(x.r)}</b>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {d.n < (d.itemCorrMinN || 0) && (
        <p className="as-hint s4">
          回答者が{d.itemCorrMinN}名未満のため、小項目ごとの影響度は表示しません（1問の回答は7段階のため、少人数では値が大きく振れます）。
        </p>
      )}
      {AREAS.map((a) => (
        <RankCard
          key={a}
          title={`事実項目【${a}】の影響度ランキング`}
          rows={items.filter((x) => x.c.area === a).sort(byR)}
          rankOf={(i) => i + 1}
          byCat
          d={d}
          last={['影響度', (x) => c2(x.r)]}
        />
      ))}
      <div className="as-card s4 ov-facts">
        <h3 className="as-card-title">
          エンゲージメントタイプごとの事実項目スコア
          <Help k="typeLayer" label="図の読み方とタイプの分け方" />
        </h3>
        {typeLines.length ? (
          <Measure className="fc-draw">
            {({ w }) => (
              <AreaColumns entries={typeEntries} w={w} aria="エンゲージメントタイプごとの、事実の中項目20項目のスコア" series={typeLines} />
            )}
          </Measure>
        ) : (
          <p className="as-hint">回答者が少ないため、表示できません。</p>
        )}
        <div className="as-legend tq-legend fc-legend">
          {TYPE_ORDER.map((t) => (
            <span key={t} className={`lg${typeOk(t) ? '' : ' off'}`}>
              <i className="mk tl" style={{ borderTopColor: TYPE_LINE_COLOR[t] }} />
              {t}タイプ（{((tc[t] && tc[t].n) || 0).toLocaleString()}名{typeOk(t) ? '' : '・少人数のため非表示'}）
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

// ------------------------------------------------------------------
// 4. 比較（改修希望 E3-11・2026-10-16 依頼側の回答で確定）：同じ回答期間の中で、区分を横に並べて見る。
//   「所属・属性」で並べる区分を選ぶ（直下の所属／役職／等級／雇用形態／出向状態／性別／年代）。
//   列：区分｜対象者数｜回答者数｜総合共感｜共感（会社）｜事実項目（会社）の中項目 5｜…｜共感（職務）｜事実項目（職務）の中項目 5。
//   1 行目は選んでいる条件の全体。行を押すと、その所属・区分に絞り込んでオーバービューを開く（相談3）。
//   少人数と二次抑制はサーバー（POST /org/compare）
const CMP_KEYS = [
  ['org', '直下の所属'],
  ['position', '役職'],
  ['levelCode', '等級'],
  ['empType', '雇用形態'],
  ['goOut', '出向状態'],
  ['sex', '性別'],
  ['age', '年代'],
];
const CMP_COLS = AREAS.flatMap((a) => [EMP_OF_UI[a], ...FACTS_OF_UI[a]]);
export const CompareTab = ({ d, query, allLabel, onDrill }) => {
  const [key, setKey] = useState('');
  const [sort, setSort] = useState('');
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const qKey = JSON.stringify(query);
  useEffect(() => {
    let active = true;
    setErr('');
    Axios.post(`${API}/org/compare`, { ...query, key })
      .then((res) => active && setData(res.data))
      .catch((e) => active && setErr((e.response && e.response.data && e.response.data.error) || '比較の取得に失敗しました。'));
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qKey, key]);
  if (err) return <div className="as-alert warn">{err}</div>;
  if (!data) return <p className="as-hint">読み込み中…</p>;
  if (data.status !== 'ok') return <p className="as-hint">比較を表示できません。</p>;
  const isOrg = data.key === 'org';
  const label = (CMP_KEYS.find(([k]) => k === data.key) || [])[1];
  const th = data.th;
  const val = (r) => (r.masked || r.total == null ? null : r.total);
  let rows = data.rows;
  if (sort === 'desc' || sort === 'asc') {
    const sg = sort === 'desc' ? -1 : 1;
    rows = rows.slice().sort((a, b) => (val(a) == null) - (val(b) == null) || sg * ((val(a) || 0) - (val(b) || 0)));
  }
  const sc = (v, m, cls) =>
    m || v == null ? (
      <td className={`num masked${cls || ''}`}>–</td>
    ) : (
      <td className={`num rk-cell ${rkCls(v)}${cls || ''}`}>
        <b>{fx1(v)}</b>
      </td>
    );
  const cells = (cats, m) =>
    CMP_COLS.map((k) => <React.Fragment key={k}>{sc(cats ? cats[k] : null, m, CAT_BY[k].kind === '共感' ? ' gs' : '')}</React.Fragment>);
  const cnt = (v) => (
    <td className="num ct">
      {v == null ? '–' : v.toLocaleString()}
      <small>名</small>
    </td>
  );
  // 「–」の行の理由（カーソルを合わせると出す）
  const why = (r) =>
    r.why === 'none'
      ? '回答者がいないため、スコアはありません。'
      : r.why === 'small'
      ? `回答者が${th}名未満のため、スコアを表示していません。`
      : r.why === 'next'
      ? `回答者は${th}名以上ですが、スコアを表示していません。「–」の区分の回答者が合わせて1名のときは、ほかの行からその1名の値を計算できないように、次に人数の少ない区分も「–」にしています。`
      : '';
  const anyMask = rows.some((r) => r.masked && r.why !== 'none');
  const tipNote = `回答者が${th}名未満の区分は、個人の特定を防ぐため、スコアを表示しません（「–」）。「–」の区分の回答者が合わせて1名のときは、ほかの行からその1名の値を計算できないように、次に人数の少ない区分も「–」にします。`;
  const tipProps = { color: 'var(--grey-10)', styles: { root: { maxWidth: 520 }, body: TIP_BODY } };
  const wrapName = (n) => {
    const i = n.indexOf('（');
    return i < 0 ? (
      n
    ) : (
      <>
        {n.slice(0, i)}
        <br />
        {n.slice(i)}
      </>
    );
  };
  return (
    <div className="g4">
      <div className="as-card s4 cmp-card">
        <div className="ttl-row">
          <h3 className="as-card-title">所属・属性ごとのスコア</h3>
          <RankLegend />
        </div>
        <div className="dist-pick">
          <label>所属・属性</label>
          <Select
            className="e3r-select dist-sel"
            value={data.key}
            onChange={setKey}
            options={[
              ...(data.keys.includes('org') ? [{ label: '所属', options: [{ value: 'org', label: '直下の所属' }] }] : []),
              { label: '属性', options: CMP_KEYS.filter(([k]) => k !== 'org' && data.keys.includes(k)).map(([k, l]) => ({ value: k, label: l })) },
            ]}
          />
          <label>並び順</label>
          <Select
            className="e3r-select dist-sel"
            value={sort}
            onChange={setSort}
            options={[
              { value: '', label: isOrg ? '組織表の順' : '登録順' },
              { value: 'desc', label: '総合共感の高い順' },
              { value: 'asc', label: '総合共感の低い順' },
            ]}
          />
          {anyMask && (
            <Tooltip title={tipNote} {...tipProps}>
              <span className="as-hint cmp-note" tabIndex={0}>
                回答{th}名未満の区分は「–」
              </span>
            </Tooltip>
          )}
        </div>
        <div className="pri-scroll">
          <table className="mtbl pri-tbl cmp-tbl">
            <thead>
              <tr>
                <th className="cn" rowSpan={2}>
                  {isOrg ? '所属' : label}
                </th>
                <th className="num ct" rowSpan={2}>
                  対象者数
                </th>
                <th className="num ct" rowSpan={2}>
                  回答者数
                </th>
                <th className="num tt" rowSpan={2}>
                  総合共感
                </th>
                {AREAS.map((a) => (
                  <React.Fragment key={a}>
                    <th className="num gs" rowSpan={2}>
                      共感
                      <br />（{a}）
                    </th>
                    <th className="num gh" colSpan={5}>
                      事実項目（{a}）
                    </th>
                  </React.Fragment>
                ))}
              </tr>
              <tr className="h2">
                {AREAS.flatMap((a) =>
                  FACTS_OF_UI[a].map((k) => (
                    <th key={k} className="num" title={`${k} ${CAT_BY[k].name}`}>
                      {wrapName(CAT_BY[k].name)}
                    </th>
                  ))
                )}
              </tr>
            </thead>
            <tbody>
              <tr className="tot">
                <td className="cn">
                  <div className="cnw">
                    <span>{allLabel}</span>
                  </div>
                </td>
                {cnt(d.eligible)}
                {cnt(d.n)}
                {sc(d.total, false, ' tt')}
                {cells(d.cats, false)}
              </tr>
              {rows.map((r) => {
                const go = !r.masked && r.n > 0;
                const nt = why(r);
                const tr = (
                  <tr
                    key={r.name}
                    className={go ? 'go' : nt ? 'nt' : undefined}
                    tabIndex={go ? 0 : undefined}
                    title={go ? `${r.name}に絞り込んで、オーバービューを開く` : undefined}
                    onClick={go ? () => onDrill(data.key, r.name) : undefined}
                    onKeyDown={go ? (e) => e.key === 'Enter' && onDrill(data.key, r.name) : undefined}
                  >
                    <td className="cn">
                      <div className="cnw">
                        <span>{r.name}</span>
                        {go && <i className="cg">›</i>}
                      </div>
                    </td>
                    {cnt(r.el)}
                    {cnt(r.n)}
                    {sc(r.total, r.masked, ' tt')}
                    {cells(r.cats, r.masked)}
                  </tr>
                );
                return nt ? (
                  <Tooltip key={r.name} title={nt} {...tipProps}>
                    {tr}
                  </Tooltip>
                ) : (
                  tr
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ------------------------------------------------------------------
// 5. 推移（改修希望 E3-12）：回答期間どうしを見る。カードは 1 つで折れ線だけ。
//   上の欄で線にするものを選ぶ（所属｜期間｜属性｜項目）。集計と少人数の扱いはサーバー（POST /org/trend）
const TREND_COLORS = [
  'var(--blue-default)',
  'var(--green-default)',
  'var(--yellow-default)',
  'var(--red-default)',
  'var(--blue-active)',
  'var(--green-active)',
  'var(--yellow-active)',
  'var(--grey-65)',
];
const TREND_ALL = '__all';
const TREND_RANGES = [
  ['', '全期間'],
  ['1', '直近1年間'],
  ['3', '直近3年間'],
];
const ATTR_LABEL = { position: '役職', levelCode: '等級', empType: '雇用形態', goOut: '出向状態', sex: '性別', age: '年代' };
// 項目：総合共感／領域ごとの共感（4）／事実の中項目（20）。中項目にマウスを重ねると右に小項目（5 問）が出る（中項目そのものも選べる）
const METRIC_OPTIONS = [
  { value: 'total', label: '総合共感' },
  {
    value: 'g:emp',
    label: '領域ごとの共感',
    children: AREAS.map((a) => {
      const k = CATS.find((c) => c.kind === '共感' && c.area === a).key;
      return { value: k, label: `共感（${a}）`, children: CAT_QS[k].map((q) => ({ value: `q${q}`, label: itemName(q) })) };
    }),
  },
  ...AREAS.map((a) => ({
    value: `g:${a}`,
    label: `事実項目（${a}）`,
    children: FACT_CATS.filter((c) => c.area === a).map((c) => ({
      value: c.key,
      label: c.name,
      children: CAT_QS[c.key].map((q) => ({ value: `q${q}`, label: itemName(q) })),
    })),
  })),
];
const metricPath = (m) => {
  if (m === 'total') return ['total'];
  const isQ = /^q\d+$/.test(m);
  const k = isQ ? CAT_OF_Q[+m.slice(1)] : m;
  const c = CAT_BY[k];
  if (!c) return ['total'];
  const g = c.kind === '共感' ? 'g:emp' : `g:${c.area}`;
  return isQ ? [g, k, m] : [g, k];
};
const metricLabel = (m) => {
  if (m === 'total') return '総合共感';
  if (/^q\d+$/.test(m)) {
    const q = +m.slice(1);
    const c = CAT_BY[CAT_OF_Q[q]];
    return `${c.kind === '共感' ? `共感（${c.area}）` : c.name} ＞ ${itemName(q)}`;
  }
  const c = CAT_BY[m];
  return c.kind === '共感' ? `共感（${c.area}）` : c.name;
};

export const TrendTab = ({ query, allLabel, questions }) => {
  const [metric, setMetric] = useState('total');
  const [range, setRange] = useState('');
  const [orgs, setOrgs] = useState(null); // null＝初めの選択（サーバーが決める）
  const [attrSel, setAttrSel] = useState([]);
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [hover, setHover] = useState(null);
  const qKey = JSON.stringify(query);
  useEffect(() => {
    let active = true;
    setErr('');
    Axios.post(`${API}/org/trend`, { ...query, metric, range, orgs, attrSel })
      .then((res) => {
        if (!active) return;
        setData(res.data);
        if (orgs == null) setOrgs(res.data.orgs);
      })
      .catch((e) => active && setErr((e.response && e.response.data && e.response.data.error) || '推移の取得に失敗しました。'));
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qKey, metric, range, JSON.stringify(orgs), JSON.stringify(attrSel)]);

  if (err) return <div className="as-alert warn">{err}</div>;
  if (!data) return <p className="as-hint">読み込み中…</p>;
  const maxLines = data.maxLines || 8;
  const extraCount = (o, a) => o.filter((x) => x !== TREND_ALL).length + a.length;
  const pickOrgs = (v) => {
    if (extraCount(v, attrSel) > maxLines) {
      message.warning(`全体のほかに選べる線は${maxLines}本までです`);
      return;
    }
    setOrgs(v);
  };
  const pickAttrs = (paths) => {
    const v = (paths || []).filter((p) => p.length === 2).map((p) => `${p[0]}::${p[1]}`);
    if (extraCount(orgs || [], v) > maxLines) {
      message.warning(`全体のほかに選べる線は${maxLines}本までです`);
      return;
    }
    setAttrSel(v);
  };
  let ci = 0;
  const series = data.series.map((s) => ({
    ...s,
    name: s.kind === 'all' ? allLabel : s.kind === 'org' ? s.label || s.key : s.label,
    main: s.kind === 'all',
    color: s.kind === 'all' ? 'var(--chart-1)' : TREND_COLORS[(ci++) % TREND_COLORS.length],
  }));
  const anyMask = series.some((s) => data.seasons.some((x) => s.why[x.id] === 'small' || s.why[x.id] === 'next'));
  const note = `回答者が${data.attrMinN}名未満の区分は、個人の特定を防ぐため、その回答期間の点と線を出しません。出さない区分の回答者が少なく、ほかの線からその値を計算できるときは、次に人数の少ない区分も出しません。`;
  const mNote = metric === 'total' ? '' : /^q\d+$/.test(metric) ? (questions && questions[+metric.slice(1)]) || '' : CAT_DEF_SHORT[metric] || (CAT_BY[metric] && CAT_BY[metric].def) || '';
  const cascaderAttrs = ATTR_KEYS_UI.filter((k) => (data.attrNames[k] || []).length).map((k) => ({
    value: k,
    label: ATTR_LABEL[k],
    children: data.attrNames[k].map((n) => ({ value: n, label: n })),
  }));
  const hp = hover ? data.seasons[hover.k] : null;
  return (
    <div className="g4">
      <div className="as-card s4 tr-card">
        <div className="ttl-row">
          <h3 className="as-card-title">スコアの推移</h3>
          {anyMask && (
            <Tooltip title={note} color="var(--grey-10)" styles={{ root: { maxWidth: 520 }, body: TIP_BODY }}>
              <span className="as-hint cmp-note" tabIndex={0}>
                回答{data.attrMinN}名未満の区分は線を出しません
              </span>
            </Tooltip>
          )}
        </div>
        <div className="dist-pick tr-pick">
          <label>所属</label>
          <Select
            className="e3r-select tr-sel tr-sel-org"
            mode="multiple"
            // 欄に入るだけ名前を出し（長い名前は 12 文字で「…」）、残りは「+N」。「+N」にマウスを重ねると残りの名前が出る。
            // 以前は欄が狭く、"responsive" だと 1 つ目の名前（グループ名＋全体）も入りきらず「+ 2 ...」だけになったため
            // 1 つ目だけにしていた。2026-10-24 に欄を広げ（E3Result.css）、名前を 12 文字までにして "responsive" に戻した
            maxTagCount="responsive"
            maxTagTextLength={12}
            maxTagPlaceholder={(omitted) => (
              <Tooltip title={omitted.map((o) => o.label).join('\n')} color="var(--grey-10)" styles={{ body: { ...TIP_BODY, whiteSpace: 'pre-line' } }}>
                <span>+{omitted.length}</span>
              </Tooltip>
            )}
            placeholder="選択なし"
            value={orgs || []}
            onChange={pickOrgs}
            options={[
              { value: TREND_ALL, label: allLabel },
              ...(data.kids.length ? [{ label: '直下の所属', options: data.kids.map((n) => ({ value: n, label: n })) }] : []),
            ]}
          />
          <label>期間</label>
          <Select className="e3r-select tr-sel-s" value={range} onChange={setRange} options={TREND_RANGES.map(([v, l]) => ({ value: v, label: l }))} />
          <label>属性</label>
          <Cascader
            className="e3r-select tr-sel"
            multiple
            expandTrigger="hover"
            maxTagCount="responsive"
            placeholder="選択なし"
            value={attrSel.map((v) => v.split('::'))}
            onChange={pickAttrs}
            options={cascaderAttrs}
            showCheckedStrategy={Cascader.SHOW_CHILD}
          />
          <label>項目</label>
          <Cascader
            className="e3r-select tr-sel"
            expandTrigger="hover"
            changeOnSelect
            allowClear={false}
            value={metricPath(metric)}
            displayRender={() => metricLabel(metric)}
            onChange={(p) => {
              const last = p && p[p.length - 1];
              if (last && !String(last).startsWith('g:')) setMetric(last);
            }}
            options={METRIC_OPTIONS}
          />
          {mNote && (
            <span className="dist-text" title={mNote}>
              {mNote}
            </span>
          )}
        </div>
        {series.length ? (
          <Measure className="tq-fill" fill>
            {(size) => <TrendChart series={series} seasons={data.seasons} size={size} onHover={setHover} />}
          </Measure>
        ) : (
          <p className="as-hint tr-none">表示する所属・属性を選んでください。</p>
        )}
        <div className="as-legend tq-legend fc-legend">
          {series.map((s) => (
            <span key={s.key} className="lg">
              <i className="mk tl" style={{ borderTopColor: s.color }} />
              {s.name}
            </span>
          ))}
          <span className="lg">{metricLabel(metric)}</span>
        </div>
        {hp && (
          <div className="tr-tip" role="status" style={{ left: hover.x + 16, top: Math.max(8, hover.y - 40) }}>
            <p className="tt-h">
              {endLabel(hp.endDate)}
              <span>{hp.name}</span>
            </p>
            <p className="tt-m">{metricLabel(metric)}</p>
            {series.map((s) => (
              <React.Fragment key={s.key}>
                <p className="tt-r">
                  <i style={{ background: s.color }} />
                  <span>{s.name}</span>
                  <b>{s.vals[hp.id] == null ? '–' : fx1(s.vals[hp.id])}</b>
                </p>
                {s.vals[hp.id] == null && s.why[hp.id] && TREND_WHY[s.why[hp.id]] && (
                  <p className="tt-w">{TREND_WHY[s.why[hp.id]](s.kind === 'attr' ? data.attrMinN : data.minN)}</p>
                )}
              </React.Fragment>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
const ATTR_KEYS_UI = ['position', 'levelCode', 'empType', 'goOut', 'sex', 'age'];

// ------------------------------------------------------------------
// 6. コメント（改修希望 E3-13）。カードは 1 つ：4 領域のタイル → 絞り込み（キーワード）→ 選んだ領域のコメントの一覧。
//   「特になし」などは件数に含めず一覧にも出さない（タイルに件数だけ）。キーワードはサーバーで絞る（返すのは最大 300 件）。
//   総合共感スコアの層（上位 50%・下位 50%）でも絞れる（E3-13 の 4）。属性で絞り込み中・どちらかの層が 6 名未満のときは使えない
//   （相談1・2026-10-16 開発側の案で確定。判定はサーバー）
const CMT_LAYERS = [
  ['', 'すべて'],
  ['hi', '総合共感スコアが高い層（上位50%）'],
  ['lo', '総合共感スコアが低い層（下位50%）'],
];
const LAYER_BLOCK = {
  attr: '属性で絞り込んでいるあいだは使えません',
  small: '上位・下位のどちらかが6名未満のため使えません',
};
export const CommentTab = ({ query }) => {
  const [area, setArea] = useState(AREAS[0]);
  const [layer, setLayer] = useState('');
  const [kwInput, setKwInput] = useState('');
  const [kw, setKw] = useState('');
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setKw(kwInput.trim()), 300);
    return () => clearTimeout(t);
  }, [kwInput]);
  const qKey = JSON.stringify(query);
  useEffect(() => {
    let active = true;
    setErr('');
    Axios.post(`${API}/org/comments`, { ...query, area, kw, layer })
      .then((res) => active && setData(res.data))
      .catch((e) => active && setErr((e.response && e.response.data && e.response.data.error) || 'コメントの取得に失敗しました。'));
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qKey, area, kw, layer]);
  if (err) return <div className="as-alert warn">{err}</div>;
  if (!data) return <p className="as-hint">読み込み中…</p>;
  const mark = (t) => {
    if (!data.kw) return t;
    const parts = t.split(data.kw);
    return parts.map((p, i) => (
      <React.Fragment key={i}>
        {i > 0 && <mark className="cm-hit">{data.kw}</mark>}
        {p}
      </React.Fragment>
    ));
  };
  let list;
  if (!data.showText)
    list = (
      <p className="as-hint cm-msg">
        {data.hiddenReason === 'small'
          ? `当てはまる回答者が${data.textMinN}名未満のため、コメント本文は表示しません（件数のみ）。`
          : 'コメント本文は表示しません（件数のみ）。'}
      </p>
    );
  else if (!data.texts.length)
    list = <p className="as-hint cm-msg">{data.kw ? 'このキーワードを含むコメントはありません。' : 'コメントはありません。'}</p>;
  else
    list = (
      <>
        <div className="cm-list">
          {data.texts.map((t, i) => (
            <div key={i} className="comment-item">
              {mark(t)}
            </div>
          ))}
        </div>
        {data.total > data.texts.length && (
          <p className="as-hint cm-msg">
            ほか {(data.total - data.texts.length).toLocaleString()}件（表示は最大{data.max}件）
          </p>
        )}
      </>
    );
  return (
    <div className="g4">
      <div className="as-card s4 cm-card">
        <h3 className="as-card-title">
          コメント
          <Help k="comment" label="コメントの表示の決まり" />
        </h3>
        <div className="cm-tiles">
          {AREAS.map((a) => {
            const s = data.stats[a] || { n: 0, none: 0, rate: null };
            const on = a === area;
            return (
              <button key={a} type="button" className={`cm-tile${on ? ' on' : ''}`} aria-pressed={on} onClick={() => setArea(a)}>
                <span className="cm-h">
                  <span className="cm-a">{a}</span>
                  <b className="cm-n num">
                    {s.n.toLocaleString()}
                    <small>件</small>
                  </b>
                </span>
                <span className="cm-s">
                  記入率 <b className="num">{s.rate == null ? '–' : s.rate.toFixed(1)}</b>%
                </span>
                <span className="cm-x">ほかに「特になし」など {s.none.toLocaleString()}件</span>
              </button>
            );
          })}
        </div>
        <div className="dist-pick">
          <label>総合共感スコア</label>
          <Select
            className="e3r-select cm-layer"
            value={data.layerBlock ? '' : layer}
            disabled={!!data.layerBlock}
            onChange={setLayer}
            popupMatchSelectWidth={false}
            options={CMT_LAYERS.map(([v, l]) => ({ value: v, label: l }))}
          />
          {data.layerBlock && <span className="as-hint">{LAYER_BLOCK[data.layerBlock]}</span>}
          <label htmlFor="cmtKw">キーワード</label>
          <Input
            id="cmtKw"
            className="cm-kw"
            allowClear
            value={kwInput}
            placeholder="含まれる語で探す"
            autoComplete="off"
            onChange={(e) => setKwInput(e.target.value)}
          />
        </div>
        <div className="pri-scroll">{list}</div>
      </div>
    </div>
  );
};
